﻿let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  TextInput,
  FlatList,
  ActivityIndicator,
  Alert,
  Modal,
  Dimensions,
  Animated,
  RefreshControl,
  Image,
  Linking, // Added Linking for apply link logic
} from 'react-native';
import { apiFetch } from '../../utils/apiClient';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useProfessional } from '../../context/ProfessionalContext';
import { useProfessionalOnboarding } from '../../context/ProfessionalOnboardingContext';
import { useGeneral } from '../../context/GeneralContext';

// ⬇️ Import Unified API Functions
import { 


  searchJobsByType, // Used to filter for part-time types
  getAPIStatus, 
  getConfiguredAPIs,
  getJobTypeStatistics 
} from '../../services/unifiedJobAPI';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');

/**
 * Helper: Transforms a job from the unifiedJobAPI into the structure
 * expected by the PartTimeJobCard component.
 */
const transformApiJob = (job) => {
  // Extract schedule/hours from title/description (best guess since API is generic)
  const titleLower = job.title?.toLowerCase() || '';
  const descLower = job.description?.toLowerCase() || '';
  
  let hours = 'Variable';
  let schedule = 'Flexible';

  if (titleLower.includes('20 hours') || descLower.includes('20 hours')) {
    hours = '20 hours/week';
  } else if (titleLower.includes('weekend') || descLower.includes('weekend')) {
    schedule = 'Weekends Only';
  }

  // Get salary info
  const minSalary = job.salary?.budgetRange?.min || 0;
  const maxSalary = job.salary?.budgetRange?.max || 0;
  const hourlyRate = job.salary?.budget || 'Competitive';
  
  // Faking weekly earnings based on min hourly rate (20 hours/week)
  const weeklyEarnings = minSalary > 100 ? `£${minSalary * 20} - £${maxSalary * 20}` : 'N/A';
  
  const isHighFlex = schedule === 'Flexible' || job.isRemote;

  return {
    id: job.id || job.jobId,
    jobId: job.jobId || job.id,
    title: job.title,
    company: job.company || job.client || 'Company',
    companyLogo: job.logo || job.clientLogo || `https://logo.clearbit.com/${job.company?.toLowerCase().replace(/\s/g, '')}.com`,
    location: job.location,
    hourlyRate: hourlyRate,
    hoursPerWeek: hours,
    schedule: schedule,
    type: job.jobType || 'Part-time',
    remote: job.isRemote,
    isRemote: job.isRemote,
    posted: job.posted || 'Recently',
    description: job.description,
    requirements: job.requirements || [],
    skills: job.skills || [],
    matchScore: job.matchScore || 75,
    featured: job.featured,
    urgent: job.urgent,
    dataSource: job.dataSource,
    applyLink: job.applyLink,
    
    // Part-time specific UI fields
    weeklyEarnings: weeklyEarnings,
    flexibilityLevel: isHighFlex ? 'High' : 'Medium',
    weekendRequired: schedule.includes('Weekend'),
    eveningWork: descLower.includes('evening'),
    companyInfo: {
      size: 'N/A',
      industry: 'Engineering/Tech',
      founded: 'N/A',
      workLifeRating: 4.5,
    },
  };
};

// --- Component Start ---
export default function PartTimeJobsScreen({ navigation }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    const _ck = `professional_${_profUserId}_parttimejobsscreen`;
    apiFetch('/api/data/jobs?job_type=part-time&is_active=true&sort=created_at&order=desc&limit=50').then(d => {
      if (d && !d.error) {
        const arr = Array.isArray(d) ? d : (d.data || d.results || []);
        AsyncStorage.setItem(_ck, JSON.stringify(arr)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  // Context hooks with safe defaults
  const { 
    userData = {}, 
    savedJobs = [], 
    jobApplications = [], 
    saveJob, 
    unsaveJob, 
    applyToJob,
    trackUserAction 
  } = useProfessional() || {};
  
  const { 
    profileData = {}, 
    scoring = {}, 
    aiMatching = {},
    runAIProfileAnalysis,
    calculateJobMatch
  } = useProfessionalOnboarding() || {};
  
  const { showToast, trackUserAction: trackGeneralAction } = useGeneral() || {};

  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState('all');
  const [isLoading, setIsLoading] = useState(true); // Start loading immediately
  const [refreshing, setRefreshing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [applyingJobId, setApplyingJobId] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({
    hoursPerWeek: 'all',
    schedule: 'all',
    location: 'all',
    remote: false,
  });

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;

  // Part-time job tabs
  const partTimeTabs = [
    { id: 'all', title: 'All Part-Time', icon: 'time-outline' },
    { id: 'recommended', title: 'For You', icon: 'star-outline' },
    { id: 'flexible', title: 'Flexible', icon: 'options-outline' },
    { id: 'saved', title: 'Saved', icon: 'bookmark-outline' },
    { id: 'applied', title: 'Applied', icon: 'checkmark-circle-outline' },
  ];

  // ⬇️ Data states (Removed mock data)
  const [allPartTimeJobs, setAllPartTimeJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);


  /**
   * Main function to load jobs from API
   */
  const loadJobsFromAPI = async () => {
    try {
      console.log('🔍 Fetching Part-Time Engineering Jobs...');
      
      const configuredAPIs = getConfiguredAPIs();
      
      if (configuredAPIs.total === 0) {
        Alert.alert('Configuration Error', 'No job search APIs are configured.');
        return;
      }

      // ✅ FIX: Use searchJobsByType('parttime') for dedicated filtering
      const result = await searchJobsByType('parttime', {
        // Broad query for part-time engineering disciplines in the UK
        query: `part-time engineer OR part-time developer OR part-time designer`,
        location: profileData?.personal?.location || 'United Kingdom',
        remote: selectedFilters.remote,
        page: 1, 
      });

      if (result.success) {
        // Transform and set jobs
        const transformedJobs = result.jobs.map(transformApiJob);
        setAllPartTimeJobs(transformedJobs);
        filterJobs(searchQuery, selectedTab, transformedJobs);
        
        console.log(`✅ Loaded ${transformedJobs.length} part-time jobs from API.`);
      } else {
        throw new Error(result.error || 'Failed to fetch part-time jobs from APIs');
      }
    } catch (error) {
      console.error('Error loading from APIs:', error);
      Alert.alert('Error', `Failed to load jobs: ${error.message}`);
    }
  };

  
  // Initialize component
  useEffect(() => {
    loadJobs();
    animateEntrance();
  }, []); // Run only once on mount

  // Reload when filters change
  useEffect(() => {
    // Re-filter when the global filter state changes
    filterJobs(searchQuery, selectedTab, allPartTimeJobs);
  }, [selectedFilters]);


  /**
   * Function to orchestrate job loading (API + filtering)
   */
  const loadJobs = async () => {
    setIsLoading(true);
    try {
      await loadJobsFromAPI();
    } catch (e) {
      // Error handled inside loadJobsFromAPI
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  };


  /**
   * Enhanced job filtering for part-time specific criteria
   */
  const filterJobs = (query = '', tab = 'all', jobsList = allPartTimeJobs) => {
    let filtered = [...jobsList];

    // Filter by search query
    if (query.trim()) {
      const searchLower = query.toLowerCase();
      filtered = filtered.filter(job =>
        String(job.title ?? '').toLowerCase().includes(searchLower) ||
        String(job.company ?? '').toLowerCase().includes(searchLower) ||
        String(job.location ?? '').toLowerCase().includes(searchLower) ||
        String(job.description ?? '').toLowerCase().includes(searchLower) ||
        (job.requirements && job.requirements.some(req => 
          req.toLowerCase().includes(searchLower)
        ))
      );
    }

    // Filter by tab
    switch (tab) {
      case 'recommended':
        filtered = filtered.filter(job => job.matchScore >= 85);
        break;
      case 'flexible':
        filtered = filtered.filter(job => job.flexibilityLevel === 'High');
        break;
      case 'saved':
        filtered = filtered.filter(job => isJobSaved(job.id));
        break;
      case 'applied':
        filtered = filtered.filter(job => isJobApplied(job.id));
        break;
    }

    // Apply additional filters
    if (selectedFilters.hoursPerWeek !== 'all') {
      filtered = filtered.filter(job => {
        // Simplistic check since API data is less structured
        const hoursString = (job.hoursPerWeek || '0-0').split('-')[1];
        const maxHours = parseInt(hoursString) || 0;
        
        switch (selectedFilters.hoursPerWeek) {
          case '10-20':
            return maxHours <= 20;
          case '20-30':
            return maxHours > 20 && maxHours <= 30;
          case '30+':
            return maxHours > 30;
          default:
            return true;
        }
      });
    }

    if (selectedFilters.schedule !== 'all') {
      filtered = filtered.filter(job => 
        String(job.schedule ?? '').toLowerCase().includes(String(selectedFilters.schedule ?? '').toLowerCase())
      );
    }

    if (selectedFilters.remote) {
      filtered = filtered.filter(job => job.remote || job.isRemote);
    }

    setFilteredJobs(filtered);
  };


  // Enhanced search handler
  const handleSearch = (query) => {
    setSearchQuery(query);
    filterJobs(query, selectedTab);
  };

  // Tab press handler
  const handleTabPress = (tab) => {
    setSelectedTab(tab.id);
    filterJobs(searchQuery, tab.id);
    
    if (trackUserAction) {
      trackUserAction('parttime_jobs_tab_change', { tab: tab.id });
    }
  };

  // Refresh handler
  const onRefresh = async () => {
    setRefreshing(true);
    await loadJobs();
  };

  // Enhanced save job functionality
  const handleSaveJob = async (job) => {
    try {
      const isSaved = isJobSaved(job.id);
      
      if (isSaved) {
        if (unsaveJob && typeof unsaveJob === 'function') {
          await unsaveJob(job.id);
          if (showToast) {
            showToast('Part-time job removed from saved jobs', 'info');
          }
        }
      } else {
        if (saveJob && typeof saveJob === 'function') {
          await saveJob(job);
          if (showToast) {
            showToast('Part-time job saved successfully!', 'success');
          }
        }
      }
      
      if (trackUserAction) {
        trackUserAction('parttime_jobs_save_toggle', { 
          jobId: job.id, 
          action: isSaved ? 'unsave' : 'save' 
        });
      }
    } catch (error) {
      console.error('❌ PART-TIME JOBS - Error saving job:', error);
      if (showToast) {
        showToast('Failed to save part-time job', 'error');
      }
    }
  };

  // Check if job is saved
  const isJobSaved = (jobId) => {
    return Array.isArray(savedJobs) && savedJobs.some(savedJob => savedJob.id === jobId);
  };

  // Check if job is applied
  const isJobApplied = (jobId) => {
    return Array.isArray(jobApplications) && jobApplications.some(app => app.jobId === jobId);
  };
  
  // Handle apply to job (kept original logic)
  const handleJobApply = async (job) => {
    // ... (Your existing handleJobApply function logic) ...
    // Note: The logic handles applying to an external link or navigating to 'SubmitForJobs'
    
    // Check if job has apply link
    const applyLink = job.applyLink || job.apply_options?.[0]?.link;

    if (applyLink) {
      Alert.alert(
        'Apply to Job',
        'This will open the job application page in your browser.',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Continue',
            onPress: async () => {
              try {
                await Linking.openURL(applyLink);
                await applyToJob(job.id || job.jobId, {
                  appliedAt: new Date().toISOString(),
                  method: 'external',
                });
                showToast?.('Application tracked', 'success');
              } catch (error) {
                console.error('Error opening apply link:', error);
                showToast?.('Could not open application link', 'error');
              }
            },
          },
        ]
      );
    } else {
      // Navigate to application screen
      navigation.navigate('JobApplication', { job });
    }
  };

  // Animate entrance
  const animateEntrance = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
      }),
    ]).start();
  };


  // Render header
  const renderHeader = () => (
    <View style={styles.header}>
      <TouchableOpacity
        style={styles.backButton}
        onPress={() => navigation.goBack()}
      >
        <Ionicons name="arrow-back" size={24} color="#ffffff" />
      </TouchableOpacity>
      
      <View style={styles.headerTitleContainer}>
        <Text style={styles.headerTitle}>Part-Time Jobs</Text>
        <Text style={styles.headerSubtitle}>{filteredJobs.length} flexible opportunities</Text>
      </View>
      
      <TouchableOpacity
        style={styles.headerActionButton}
        onPress={() => setShowFilters(true)}
      >
        <Ionicons name="options" size={20} color="#ffffff" />
      </TouchableOpacity>
    </View>
  );

  // Render search bar
  const renderSearchBar = () => (
    <View style={styles.searchContainer}>
      <View style={styles.searchInputContainer}>
        <Ionicons name="search" size={20} color="#64748B" />
        <TextInput
          style={styles.searchInput}
          placeholder="Search part-time jobs, skills, or companies..."
          value={searchQuery}
          onChangeText={handleSearch}
          placeholderTextColor="#94a3b8"
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => handleSearch('')}>
            <Ionicons name="close-circle" size={20} color="#64748B" />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );

  // Render part-time benefits
  const renderPartTimeBenefits = () => (
    <View style={styles.benefitsContainer}>
      <Text style={styles.benefitsTitle}>Part-Time Benefits</Text>
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.benefitsScrollContainer}
      >
        {[
          { icon: 'time', title: 'Flexible Hours', color: '#43e97b' },
          { icon: 'home', title: 'Work-Life Balance', color: '#f59e0b' },
          { icon: 'school', title: 'Study Compatible', color: '#8b5cf6' },
          { icon: 'cash', title: 'Extra Income', color: '#10b981' },
        ].map((benefit, index) => (
          <View key={index} style={[styles.benefitCard, { borderColor: benefit.color }]}>
            <View style={[styles.benefitIcon, { backgroundColor: benefit.color }]}>
              <Ionicons name={benefit.icon} size={16} color="#ffffff" />
            </View>
            <Text style={styles.benefitTitle}>{benefit.title}</Text>
          </View>
        ))}
      </ScrollView>
    </View>
  );

  // Render tabs
  const renderTabs = () => (
    <View style={styles.tabsContainer}>
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.tabsScrollContainer}
      >
        {partTimeTabs.map((tab) => {
          const jobCount = tab.id === 'all' ? filteredJobs.length :
                          tab.id === 'recommended' ? filteredJobs.filter(job => job.matchScore >= 85).length :
                          tab.id === 'flexible' ? filteredJobs.filter(job => job.flexibilityLevel === 'High').length :
                          tab.id === 'saved' ? filteredJobs.filter(job => isJobSaved(job.id)).length :
                          tab.id === 'applied' ? filteredJobs.filter(job => isJobApplied(job.id)).length : 0;

          return (
            <TouchableOpacity
              key={tab.id}
              style={[
                styles.tab,
                selectedTab === tab.id && styles.activeTab,
              ]}
              onPress={() => handleTabPress(tab)}
            >
              <Ionicons 
                name={tab.icon} 
                size={16} 
                color={selectedTab === tab.id ? "#ffffff" : "#64748B"} 
              />
              <Text style={[
                styles.tabText,
                selectedTab === tab.id && styles.activeTabText,
              ]}>
                {tab.title}
              </Text>
              {jobCount > 0 && (
                <View style={[
                  styles.tabBadge,
                  selectedTab === tab.id && styles.activeTabBadge,
                ]}>
                  <Text style={[
                    styles.tabBadgeText,
                    selectedTab === tab.id && styles.activeTabBadgeText,
                  ]}>
                    {jobCount}
                  </Text>
                </View>
              )}
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );

  // Render part-time job card
  const renderPartTimeJobCard = ({ item: job }) => {
    const isSaved = isJobSaved(job.id);
    const isApplied = isJobApplied(job.id);
    const isApplying = applyingJobId === job.id;

    return (
      <Animated.View 
        style={[
          styles.jobCard,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          },
        ]}
      >
        {/* Job badges */}
        <View style={styles.jobBadges}>
          <View style={[styles.badge, styles.partTimeBadge]}>
            <Ionicons name="time" size={12} color="#ffffff" />
            <Text style={styles.badgeText}>Part-Time</Text>
          </View>
          {job.featured && (
            <View style={[styles.badge, styles.featuredBadge]}>
              <Ionicons name="star" size={12} color="#ffffff" />
              <Text style={styles.badgeText}>Featured</Text>
            </View>
          )}
          {job.urgent && (
            <View style={[styles.badge, styles.urgentBadge]}>
              <Ionicons name="flash" size={12} color="#ffffff" />
              <Text style={styles.badgeText}>Urgent</Text>
            </View>
          )}
          {(job.remote || job.isRemote) && (
            <View style={[styles.badge, styles.remoteBadge]}>
              <Ionicons name="home" size={12} color="#ffffff" />
              <Text style={styles.badgeText}>Remote</Text>
            </View>
          )}
          {job.flexibilityLevel === 'High' && (
            <View style={[styles.badge, styles.flexibleBadge]}>
              <Ionicons name="options" size={12} color="#ffffff" />
              <Text style={styles.badgeText}>Flexible</Text>
            </View>
          )}
        </View>

        {/* Company info */}
        <View style={styles.jobHeader}>
          <View style={styles.companyLogoContainer}>
            {job.companyLogo ? (
              <Image 
                source={{ uri: (job.companyLogo && typeof job.companyLogo=="string" && job.companyLogo.startsWith("http")) ? job.companyLogo : undefined }} 
                style={styles.companyLogo}
                onError={() => console.warn('Failed to load company logo for:', job.company)}
              />
            ) : (
              <View style={styles.companyLogoPlaceholder}>
                <Text style={styles.companyLogoText}>
                  {job.company.charAt(0)}
                </Text>
              </View>
            )}
          </View>

          <View style={styles.jobInfo}>
            <Text style={styles.jobTitle} numberOfLines={2}>{job.title}</Text>
            <Text style={styles.companyName}>{job.company}</Text>
            
            <View style={styles.jobMeta}>
              <View style={styles.metaItem}>
                <Ionicons name="location" size={12} color="#64748B" />
                <Text style={styles.metaText}>{job.location}</Text>
              </View>
              <View style={styles.metaItem}>
                <Ionicons name="cash" size={12} color="#64748B" />
                <Text style={styles.metaText}>{job.hourlyRate}</Text>
              </View>
            </View>
          </View>

          <TouchableOpacity
            style={styles.saveButton}
            onPress={() => handleSaveJob(job)}
          >
            <Ionicons 
              name={isSaved ? "bookmark" : "bookmark-outline"} 
              size={20} 
              color={isSaved ? "#f59e0b" : "#64748B"} 
            />
          </TouchableOpacity>
        </View>

        {/* Job description */}
        <Text style={styles.jobDescription} numberOfLines={2}>
          {job.description}
        </Text>

        {/* Part-time specific info */}
        <View style={styles.partTimeInfoContainer}>
          <View style={styles.partTimeInfoItem}>
            <Ionicons name="time-outline" size={14} color="#43e97b" />
            <Text style={styles.partTimeInfoText}>{job.hoursPerWeek}</Text>
          </View>
          <View style={styles.partTimeInfoItem}>
            <Ionicons name="calendar-outline" size={14} color="#43e97b" />
            <Text style={styles.partTimeInfoText}>{job.schedule}</Text>
          </View>
          <View style={styles.partTimeInfoItem}>
            <Ionicons name="trending-up-outline" size={14} color="#43e97b" />
            <Text style={styles.partTimeInfoText}>{job.weeklyEarnings}</Text>
          </View>
        </View>

        {/* Job requirements */}
        <View style={styles.requirementsContainer}>
          <ScrollView 
            horizontal 
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.requirementsScrollContainer}
          >
            {job.requirements && job.requirements.slice(0, 4).map((requirement, index) => (
              <View key={index} style={styles.requirementTag}>
                <Text style={styles.requirementText}>{requirement}</Text>
              </View>
            ))}
          </ScrollView>
        </View>

        {/* Match score and actions */}
        <View style={styles.jobFooter}>
          <View style={styles.matchContainer}>
            <View style={styles.matchScore}>
              <Text style={styles.matchText}>{job.matchScore}% match</Text>
            </View>
            <Text style={styles.postedTime}>{job.posted}</Text>
          </View>

          <View style={styles.actionButtons}>
            <TouchableOpacity
              style={styles.detailsButton}
              onPress={() => {
                try {
                  navigation.navigate('JobDetails', { 
                    jobId: job.id,
                    id: job.id,
                    job: job,
                    source: 'PartTimeJobsScreen'
                  });
                } catch (error) {
                  console.error('❌ PART-TIME JOBS - Navigation to JobDetailsScreen failed:', error);
                  Alert.alert('Error', 'Unable to view job details. Please try again.');
                }
              }}
            >
              <Ionicons name="information-circle-outline" size={16} color="#43e97b" />
              <Text style={styles.detailsButtonText}>Details</Text>
            </TouchableOpacity>

            {/* Apply Now button with proper navigation */}
            <TouchableOpacity
              style={[
                styles.applyButton,
                isApplied && styles.appliedButton,
                isApplying && styles.applyingButton,
              ]}
              onPress={() => handleJobApply(job)}
              disabled={isApplied || isApplying}
            >
              {isApplying ? (
                <>
                  <ActivityIndicator size={20} color="#ffffff" />
                  <Text style={styles.applyButtonText}>Applying...</Text>
                </>
              ) : (
                <>
                  {isApplied ? (
                    <Ionicons name="checkmark-circle" size={16} color="#ffffff" />
                  ) : (
                    <Ionicons name="paper-plane" size={16} color="#ffffff" />
                  )}
                  <Text style={[styles.applyButtonText, isApplied && styles.appliedButtonText]}>
                    {isApplied ? 'Applied' : 'Apply Now'}
                  </Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </Animated.View>
    );
  };

  // Render empty state
  const renderEmptyState = () => (
    <View style={styles.emptyContainer}>
      <Ionicons name="time-outline" size={64} color="#94a3b8" />
      <Text style={styles.emptyTitle}>No Part-Time Jobs Found</Text>
      <Text style={styles.emptyMessage}>
        Try adjusting your search criteria or check back later for new part-time opportunities.
      </Text>
      <TouchableOpacity
        style={styles.refreshButton}
        onPress={() => {
          setSearchQuery('');
          setSelectedTab('all');
          filterJobs('', 'all');
        }}
      >
        <Ionicons name="refresh" size={16} color="#43e97b" />
        <Text style={styles.refreshButtonText}>Reset Filters</Text>
      </TouchableOpacity>
    </View>
  );

  // Render filter modal
  const renderFilterModal = () => (
    <Modal
      visible={showFilters}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowFilters(false)}
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Part-Time Job Filters</Text>
          <TouchableOpacity
            style={styles.modalCloseButton}
            onPress={() => setShowFilters(false)}
          >
            <Ionicons name="close" size={24} color="#64748B" />
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.modalContent}>
          {/* Hours Per Week Filter */}
          <View style={styles.filterSection}>
            <Text style={styles.filterTitle}>Hours Per Week</Text>
            <View style={styles.filterOptions}>
              {['all', '10-20', '20-30', '30+'].map((hours) => (
                <TouchableOpacity
                  key={hours}
                  style={[
                    styles.filterOption,
                    selectedFilters.hoursPerWeek === hours && styles.selectedFilterOption,
                  ]}
                  onPress={() => setSelectedFilters(prev => ({ ...prev, hoursPerWeek: hours }))}
                >
                  <Text style={[
                    styles.filterOptionText,
                    selectedFilters.hoursPerWeek === hours && styles.selectedFilterOptionText,
                  ]}>
                    {hours === 'all' ? 'All Hours' : `${hours} hours`}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Schedule Filter */}
          <View style={styles.filterSection}>
            <Text style={styles.filterTitle}>Schedule Type</Text>
            <View style={styles.filterOptions}>
              {['all', 'flexible', 'fixed', 'weekends'].map((schedule) => (
                <TouchableOpacity
                  key={schedule}
                  style={[
                    styles.filterOption,
                    selectedFilters.schedule === schedule && styles.selectedFilterOption,
                  ]}
                  onPress={() => setSelectedFilters(prev => ({ ...prev, schedule: schedule }))}
                >
                  <Text style={[
                    styles.filterOptionText,
                    selectedFilters.schedule === schedule && styles.selectedFilterOptionText,
                  ]}>
                    {schedule.charAt(0).toUpperCase() + schedule.slice(1)}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Remote Work Filter */}
          <View style={styles.filterSection}>
            <Text style={styles.filterTitle}>Work Type</Text>
            <TouchableOpacity
              style={[
                styles.toggleOption,
                selectedFilters.remote && styles.selectedToggleOption,
              ]}
              onPress={() => setSelectedFilters(prev => ({ ...prev, remote: !prev.remote }))}
            >
              <Ionicons 
                name={selectedFilters.remote ? "checkbox" : "square-outline"} 
                size={20} 
                color={selectedFilters.remote ? "#43e97b" : "#64748B"} 
              />
              <Text style={styles.toggleOptionText}>Remote Work Only</Text>
            </TouchableOpacity>
          </View>

          {/* Apply Filters Button */}
          <TouchableOpacity
            style={styles.applyFiltersButton}
            onPress={() => {
              filterJobs(searchQuery, selectedTab);
              setShowFilters(false);
            }}
          >
            <Text style={styles.applyFiltersButtonText}>Apply Filters</Text>
          </TouchableOpacity>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#43e97b" />

      {renderHeader()}

      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#43e97b" />
          <Text style={styles.loadingText}>Loading Part-Time Engineering Jobs...</Text>
        </View>
      ) : (
        <ScrollView
          style={styles.content}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#43e97b" />
          }
        >
          {renderSearchBar()}
          {renderPartTimeBenefits()}
          {renderTabs()}

          <View style={styles.jobsContainer}>
            <Text style={styles.jobsTitle}>
              {partTimeTabs.find(tab => tab.id === selectedTab)?.title || 'Part-Time Jobs'} ({filteredJobs.length})
            </Text>
            
            {filteredJobs.length > 0 ? (
              <FlatList
                data={filteredJobs}
                renderItem={renderPartTimeJobCard}
                keyExtractor={(item) => item.id}
                scrollEnabled={false}
                showsVerticalScrollIndicator={false}
              />
            ) : (
              renderEmptyState()
            )}
          </View>
        </ScrollView>
      )}

      {renderFilterModal()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0e4e96',
  },
  // ADDED Loading styles for consistency
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#64748B',
    fontWeight: '500',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#0e4e96',
  },
  backButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.15)',
  },
  headerTitleContainer: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: 2,
  },
  headerSubtitle: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.8)',
    fontWeight: '500',
  },
  headerActionButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.15)',
  },
  content: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  searchContainer: {
    backgroundColor: '#ffffff',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#1e293b',
    marginLeft: 12,
  },
  benefitsContainer: {
    backgroundColor: '#ffffff',
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  benefitsTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: 16,
    paddingHorizontal: 20,
  },
  benefitsScrollContainer: {
    paddingHorizontal: 20,
  },
  benefitCard: {
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 12,
    marginRight: 12,
    borderWidth: 2,
    minWidth: 80,
  },
  benefitIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  benefitTitle: {
    fontSize: 11,
    color: '#1e293b',
    fontWeight: '600',
    textAlign: 'center',
  },
  tabsContainer: {
    backgroundColor: '#ffffff',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  tabsScrollContainer: {
    paddingHorizontal: 20,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 10,
    marginRight: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  activeTab: {
    backgroundColor: '#43e97b',
    borderColor: '#43e97b',
  },
  tabText: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '500',
    marginLeft: 6,
  },
  activeTabText: {
    color: '#ffffff',
  },
  tabBadge: {
    backgroundColor: '#e2e8f0',
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginLeft: 6,
  },
  activeTabBadge: {
    backgroundColor: 'rgba(255,255,255,0.3)',
  },
  tabBadgeText: {
    fontSize: 10,
    color: '#64748B',
    fontWeight: '600',
  },
  activeTabBadgeText: {
    color: '#ffffff',
  },
  jobsContainer: {
    backgroundColor: '#ffffff',
    marginTop: 8,
    paddingHorizontal: 20,
    paddingVertical: 20,
  },
  jobsTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: 16,
  },
  jobCard: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  jobBadges: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 8,
  },
  partTimeBadge: {
    backgroundColor: '#43e97b',
  },
  featuredBadge: {
    backgroundColor: '#f59e0b',
  },
  urgentBadge: {
    backgroundColor: '#ef4444',
  },
  remoteBadge: {
    backgroundColor: '#10b981',
  },
  flexibleBadge: {
    backgroundColor: '#8b5cf6',
  },
  badgeText: {
    fontSize: 10,
    color: '#ffffff',
    fontWeight: '600',
    marginLeft: 4,
  },
  jobHeader: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  companyLogoContainer: {
    marginRight: 12,
  },
  companyLogo: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: '#f1f5f9',
  },
  companyLogoPlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: '#43e97b',
    justifyContent: 'center',
    alignItems: 'center',
  },
  companyLogoText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  jobInfo: {
    flex: 1,
  },
  jobTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: 4,
    lineHeight: 22,
  },
  companyName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#43e97b',
    marginBottom: 8,
  },
  jobMeta: {
    flexDirection: 'row',
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
  },
  metaText: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 4,
    fontWeight: '500',
  },
  saveButton: {
    padding: 8,
  },
  jobDescription: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
    marginBottom: 12,
  },
  partTimeInfoContainer: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  partTimeInfoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0fdf4',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#bbf7d0',
  },
  partTimeInfoText: {
    fontSize: 11,
    color: '#166534',
    fontWeight: '600',
    marginLeft: 4,
  },
  requirementsContainer: {
    marginBottom: 16,
  },
  requirementsScrollContainer: {
    paddingRight: 20,
  },
  requirementTag: {
    backgroundColor: '#eff6ff',
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#bfdbfe',
  },
  requirementText: {
    fontSize: 12,
    color: '#1e40af',
    fontWeight: '500',
  },
  jobFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  matchContainer: {
    flex: 1,
  },
  matchScore: {
    backgroundColor: '#dcfce7',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
    alignSelf: 'flex-start',
    marginBottom: 4,
  },
  matchText: {
    fontSize: 12,
    color: '#166534',
    fontWeight: '600',
  },
  postedTime: {
    fontSize: 11,
    color: '#94a3b8',
    fontWeight: '500',
  },
  actionButtons: {
    flexDirection: 'row',
  },
  detailsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0fdf4',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 12,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#bbf7d0',
  },
  detailsButtonText: {
    fontSize: 12,
    color: '#43e97b',
    fontWeight: '600',
    marginLeft: 4,
  },
  applyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#43e97b',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 12,
  },
  appliedButton: {
    backgroundColor: '#10b981',
  },
  applyingButton: {
    backgroundColor: '#94a3b8',
  },
  applyButtonText: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: '600',
    marginLeft: 4,
  },
  appliedButtonText: {
    color: '#ffffff',
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1e293b',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyMessage: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
    paddingHorizontal: 20,
  },
  refreshButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0fdf4',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#bbf7d0',
  },
  refreshButtonText: {
    fontSize: 14,
    color: '#43e97b',
    fontWeight: '600',
    marginLeft: 8,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1e293b',
  },
  modalCloseButton: {
    padding: 8,
  },
  modalContent: {
    flex: 1,
    paddingHorizontal: 20,
  },
  filterSection: {
    marginVertical: 20,
  },
  filterTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: 12,
  },
  filterOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  filterOption: {
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 10,
    marginRight: 8,
    marginBottom: 8,
  },
  selectedFilterOption: {
    backgroundColor: '#43e97b',
    borderColor: '#43e97b',
  },
  filterOptionText: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '500',
  },
  selectedFilterOptionText: {
    color: '#ffffff',
  },
  toggleOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
  },
  selectedToggleOption: {
    backgroundColor: '#f0fdf4',
  },
  toggleOptionText: {
    fontSize: 14,
    color: '#1e293b',
    fontWeight: '500',
    marginLeft: 12,
  },
  applyFiltersButton: {
    backgroundColor: '#43e97b',
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: 'center',
    marginVertical: 20,
  },
  applyFiltersButtonText: {
    fontSize: 16,
    color: '#ffffff',
    fontWeight: '600',
  },
});